import React, { useEffect } from 'react';
import {
  X,
  CheckCircle2,
  BarChart3,
  FileText,
  ShieldCheck,
  Target,
  Zap,
  Link2,
  Sliders,
  TrendingDown,
  Info,
  AlertCircle,
} from 'lucide-react';
import { KpiMetricItem, KpiChartDataPoint, KpiTableRow } from '../types';

interface KpiDetailModalProps {
  kpi: KpiMetricItem | null;
  onClose: () => void;
}

export const KpiDetailModal: React.FC<KpiDetailModalProps> = ({ kpi, onClose }) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (!kpi) return null;

  const breakdown = kpi.breakdown;
  const isNotAvailable = kpi.status === 'not_available';

  const getMetricIcon = (id: string) => {
    switch (id) {
      case 'signal_recall':
        return <Target className="w-5 h-5 text-amber-600" />;
      case 'signal_precision':
        return <Sliders className="w-5 h-5 text-amber-600" />;
      case 'monitoring_time':
        return <TrendingDown className="w-5 h-5 text-amber-600" />;
      case 'detection_latency':
        return <Zap className="w-5 h-5 text-blue-600" />;
      case 'source_attribution':
        return <Link2 className="w-5 h-5 text-emerald-600" />;
      default:
        return <ShieldCheck className="w-5 h-5 text-emerald-600" />;
    }
  };

  const renderStatusBadge = () => {
    if (isNotAvailable) {
      return (
        <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-800 text-[10px] font-bold px-2 py-0.5 rounded-full border border-amber-200/70">
          <AlertCircle className="w-3 h-3 text-amber-600" />
          {kpi.statusLabel}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded-full border border-emerald-200/70">
        <CheckCircle2 className="w-3 h-3 text-emerald-600" />
        {kpi.statusLabel}
      </span>
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/60 backdrop-blur-xs flex items-center justify-center p-4 sm:p-6 animate-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="kpi-modal-title"
    >
      <div
        className="bg-white rounded-2xl border border-slate-200/90 shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden text-slate-900"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Top Header */}
        <div className="p-6 pb-4 border-b border-slate-100 flex items-start justify-between gap-4 bg-slate-50/50">
          <div className="flex items-start gap-3 min-w-0">
            <div className="p-2.5 rounded-xl bg-white border border-slate-200/80 shadow-2xs shrink-0 mt-0.5">
              {getMetricIcon(kpi.id)}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 font-mono">
                  {isNotAvailable ? 'Observability Gap' : 'Live Operational Metric'}
                </span>
                {renderStatusBadge()}
              </div>
              <h2
                id="kpi-modal-title"
                className="text-xl font-bold text-slate-900 tracking-tight mt-0.5"
              >
                {kpi.name}
              </h2>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-100 text-slate-800 border border-slate-200">
                  <span className="text-slate-500 uppercase tracking-wider text-[10px] font-mono">
                    Target Goal:
                  </span>
                  <span>{kpi.target}</span>
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-50 text-slate-700 border border-slate-200">
                  <span className="text-slate-500 uppercase tracking-wider text-[10px] font-mono">
                    Measurement:
                  </span>
                  <span
                    className={
                      isNotAvailable ? 'text-slate-400 font-medium' : 'text-slate-900 font-bold'
                    }
                  >
                    {kpi.actual}
                  </span>
                </span>
              </div>
              <p className="text-xs text-slate-600 mt-2 max-w-2xl leading-relaxed">
                {kpi.description}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            aria-label="Close modal"
            className="w-8 h-8 rounded-lg bg-white hover:bg-slate-100 border border-slate-200 text-slate-400 hover:text-slate-700 flex items-center justify-center transition cursor-pointer shrink-0 shadow-2xs"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Modal Scrollable Body */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1 text-xs">
          {/* Telemetry Gap Advisory Alert when metric is not available */}
          {isNotAvailable && (
            <div className="bg-amber-50/80 border border-amber-200/80 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-xs space-y-1">
                <h4 className="font-bold text-amber-900">
                  Telemetry Required for Accurate Measurement
                </h4>
                <p className="text-amber-800 leading-relaxed text-[11px]">
                  This metric cannot be calculated honestly from internal ingest logs alone without
                  external ground-truth baselines. To prevent deceptive reporting, this metric is
                  flagged as <strong>Telemetry Pending</strong> until the prerequisites below are
                  tracked.
                </p>
              </div>
            </div>
          )}

          {/* Highlight Stats Grid */}
          {breakdown?.highlightStats && breakdown.highlightStats.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {breakdown.highlightStats.map((stat, idx) => (
                <div
                  key={idx}
                  className="bg-slate-50/70 rounded-xl border border-slate-200/70 p-3.5 flex flex-col justify-between"
                >
                  <span className="text-[11px] font-medium text-slate-500 truncate">
                    {stat.label}
                  </span>
                  <div className="mt-2 text-xl font-bold text-slate-900 font-sans tracking-tight">
                    {stat.value}
                  </div>
                  {stat.subtext && (
                    <span className="text-[10px] text-slate-500 mt-0.5 truncate">
                      {stat.subtext}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Visual Distribution Chart */}
          {breakdown?.chartData && breakdown.chartData.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200/90 p-5 shadow-2xs space-y-4">
              <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-blue-600" />
                  <h3 className="font-bold text-slate-900 text-xs tracking-tight">
                    {breakdown.chartTitle}
                  </h3>
                </div>
                <div className="flex items-center gap-3 text-[10px] text-slate-500">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-xs bg-emerald-500"></span>
                    Live Data Volume
                  </span>
                </div>
              </div>

              {/* Horizontal Comparative Chart Bars */}
              <div className="space-y-3.5 pt-1">
                {(() => {
                  const maxVal = Math.max(
                    ...breakdown.chartData.map((d: KpiChartDataPoint) => d.actual),
                    1
                  );
                  return breakdown.chartData.map((dp: KpiChartDataPoint, idx: number) => {
                    const percent = Math.min(100, Math.max(6, (dp.actual / maxVal) * 100));

                    return (
                      <div key={idx} className="space-y-1">
                        <div className="flex items-center justify-between text-[11px]">
                          <span className="font-medium text-slate-700">{dp.label}</span>
                          <div className="flex items-center gap-2 font-mono text-[11px]">
                            <span className="font-bold text-slate-900">{dp.formattedActual}</span>
                            {dp.formattedTarget && (
                              <span className="text-slate-400 text-[10px]">
                                ({dp.formattedTarget})
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden flex">
                          <div
                            className="bg-emerald-500 h-2 rounded-full transition-all duration-700"
                            style={{ width: `${percent}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}

          {/* Detailed Audit Data Table */}
          {breakdown?.tableRows && breakdown.tableRows.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200/90 overflow-hidden shadow-2xs">
              <div className="p-4 bg-slate-50/70 border-b border-slate-100 flex items-center gap-2">
                <FileText className="w-4 h-4 text-slate-600" />
                <h3 className="font-bold text-slate-900 text-xs tracking-tight">
                  {breakdown.tableTitle}
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs divide-y divide-slate-100">
                  <thead className="bg-slate-50/50 text-[10px] font-bold text-slate-500 uppercase tracking-wider font-mono">
                    <tr>
                      <th className="px-4 py-2.5">{breakdown.tableHeaders[0]}</th>
                      <th className="px-4 py-2.5">{breakdown.tableHeaders[1]}</th>
                      <th className="px-4 py-2.5 text-right">{breakdown.tableHeaders[2]}</th>
                      <th className="px-4 py-2.5 text-right">{breakdown.tableHeaders[3]}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-[11px]">
                    {breakdown.tableRows.map((row: KpiTableRow, idx: number) => (
                      <tr key={idx} className="hover:bg-slate-50/70 transition">
                        <td className="px-4 py-2.5 font-semibold text-slate-900 whitespace-nowrap">
                          {row.category}
                        </td>
                        <td className="px-4 py-2.5 text-slate-600">{row.detail}</td>
                        <td className="px-4 py-2.5 text-right font-mono font-medium text-slate-800 whitespace-nowrap">
                          {row.metric}
                        </td>
                        <td className="px-4 py-2.5 text-right whitespace-nowrap">
                          <span
                            className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
                              row.status === 'Required' || row.status === 'Action Needed'
                                ? 'text-amber-800 bg-amber-50 border-amber-200/60'
                                : 'text-emerald-800 bg-emerald-50 border-emerald-200/60'
                            }`}
                          >
                            {row.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Audit Methodology & Contractual Reference Box */}
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200/80 space-y-2 text-slate-600">
            <div className="flex items-center gap-1.5 text-slate-900 font-semibold text-xs">
              <Info className="w-3.5 h-3.5 text-blue-600" />
              <span>Data Source & Live Telemetry Methodology</span>
            </div>
            <p className="text-[11px] leading-relaxed">
              {breakdown?.methodology || kpi.description}
            </p>
            {breakdown?.complianceClause && (
              <div className="pt-2 border-t border-slate-200/60 text-[10px] font-mono text-slate-500">
                <strong>Source Ledger: </strong>
                <span>{breakdown.complianceClause}</span>
              </div>
            )}
          </div>
        </div>

        {/* Modal Bottom Action Bar */}
        <div className="p-4 px-6 border-t border-slate-100 bg-slate-50/80 flex items-center justify-between">
          <span className="text-[11px] text-slate-500 font-mono truncate">
            Source: {kpi.benchmarkSource}
          </span>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold transition cursor-pointer shadow-xs"
          >
            Close Detail View
          </button>
        </div>
      </div>
    </div>
  );
};
